import PropTypes from 'prop-types';

/**
 * Input Component with Floating Label (Preline UI)
 *
 * Modern input component with floating label that moves up when the field has content.
 * Follows Preline UI design patterns.
 *
 * @param {Object} props
 * @param {string} props.label - Floating label text
 * @param {string} props.value - Input value (controlled)
 * @param {Function} props.onChange - Callback when value changes (receives value directly)
 * @param {string} props.type - Input type (text, email, password, etc.)
 * @param {string} props.error - Error message to display
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.autoComplete - Autocomplete attribute
 * @param {boolean} props.autoFocus - Whether to autofocus the input
 * @param {React.ReactNode} props.endAdornment - Element to show at the end (e.g., password toggle button)
 * @param {string} props.placeholder - Placeholder text (for floating label functionality)
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input id attribute
 */
export function Input({
  label,
  value,
  onChange,
  type = 'text',
  error,
  required = false,
  disabled = false,
  autoComplete,
  autoFocus = false,
  endAdornment,
  placeholder,
  name,
  id,
}) {
  const inputId = id || name || label?.toLowerCase().replace(/\s+/g, '-');
  const placeholderText = placeholder || '';

  // Base classes for the input with floating label support
  const inputClasses = `
    peer p-4 block w-full border-border-default rounded-xl text-sm
    placeholder:transparent
    focus:border-brand focus:ring-brand
    disabled:opacity-50 disabled:pointer-events-none
    focus:pt-6 focus:pb-2
    [&:not(:placeholder-shown)]:pt-6 [&:not(:placeholder-shown)]:pb-2
    autofill:pt-6 autofill:pb-2
    ${endAdornment ? 'pe-11' : ''}
    ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  // Label classes for floating animation
  const labelClasses = `
    absolute top-0 start-0 p-4 h-full text-sm truncate pointer-events-none
    transition ease-in-out duration-100 border border-transparent origin-[0_0]
    peer-disabled:opacity-50 peer-disabled:pointer-events-none
    peer-focus:scale-90 peer-focus:translate-x-0.5 peer-focus:-translate-y-1.5 peer-focus:text-muted
    peer-[:not(:placeholder-shown)]:scale-90 peer-[:not(:placeholder-shown)]:translate-x-0.5
    peer-[:not(:placeholder-shown)]:-translate-y-1.5 peer-[:not(:placeholder-shown)]:text-muted
    ${error ? 'peer-focus:text-red-500 peer-[:not(:placeholder-shown)]:text-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="w-full">
      {/* Input Container */}
      <div className="relative">
        <input
          id={inputId}
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholderText}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={inputClasses}
        />

        {/* Floating Label */}
        <label htmlFor={inputId} className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {/* End Adornment (e.g., password toggle button) */}
        {endAdornment && (
          <div className="absolute inset-y-0 end-0 flex items-center pointer-events-none">
            <div className="pointer-events-auto">
              {endAdornment}
            </div>
          </div>
        )}
      </div>

      {/* Error Message (Preline UI) */}
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

Input.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  endAdornment: PropTypes.node,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
};
